import React, { useContext, useEffect, useState } from "react";
import { Table, Button, Space, Tag, message } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import useAxiosInstance from "../../lib/useAxiosInstance";
import PlaybookContext from "../../context/playbookContext";
import DeleteModal from "../DeleteModal.jsx";

const CATEGORY_COLORS = { piu: "blue", electionMode: "purple" };
const SCOPE_COLORS = { state: "gold", parliamentary: "cyan", assembly: "magenta" };
const KIND_COLORS = { parliamentary: "cyan", assembly: "magenta", block: "volcano" };

const PlaybookList = ({ refreshKey }) => {
  const axiosInstance = useAxiosInstance();
  const playbookContext = useContext(PlaybookContext);

  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);

  const fetchPlaybooks = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("api/playbook");
      setPlaybooks(res.data?.data || []);
    } catch (error) {
      message.error("Failed to load playbooks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlaybooks();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdit = (record) => {
    playbookContext.updateEditData(record);
    playbookContext.updateAddMode(false);
    playbookContext.updateOpenModal(true);
  };

  const openDeleteModal = (record) => {
    setSelectedPlaybook(record);
    setIsDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setSelectedPlaybook(null);
    setIsDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!selectedPlaybook) return;
    try {
      await axiosInstance.delete(`api/playbook/${selectedPlaybook._id}`);
      message.success("Playbook deleted successfully");
      fetchPlaybooks();
    } catch (error) {
      message.error("Failed to delete playbook");
    } finally {
      closeDeleteModal();
    }
  };

  const getClientLabel = (client) => {
    if (!client) return "—";
    const fullName = `${client.adminFirstName || ""} ${client.adminLastName || ""}`.trim();
    return fullName || client.organisationName || client.adminEmail || "—";
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => (
        <Tag color={CATEGORY_COLORS[category] || "default"}>
          {category === "electionMode" ? "Election Mode" : "PIU"}
        </Tag>
      ),
    },
    {
      title: "Type / Scope",
      key: "typeOrScope",
      render: (_, record) =>
        record.category === "electionMode" ? (
          <Space size={4}>
            <Tag color={SCOPE_COLORS[record.electionScope] || "default"}>
              {record.electionScope}
            </Tag>
            {record.reportKind && (
              <Tag color={KIND_COLORS[record.reportKind] || "default"}>
                {record.reportKind}
              </Tag>
            )}
          </Space>
        ) : (
          <Tag color={record.type === "village" ? "green" : "blue"}>
            {record.type?.charAt(0).toUpperCase() + record.type?.slice(1)}
          </Tag>
        ),
    },
    {
      title: "Constituency / Block",
      key: "matchKeys",
      render: (_, record) =>
        [record.constituency, record.block].filter(Boolean).join(" / ") || "—",
    },
    {
      title: "Clients",
      key: "clients",
      render: (_, record) => {
        const list = record.clients?.length
          ? record.clients
          : record.client
          ? [record.client]
          : [];
        if (!list.length) return "—";
        return (
          <Space wrap size={4}>
            {list.map((c, i) => (
              <Tag key={c?._id || i}>{getClientLabel(c)}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Uploaded On",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString("en-IN"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => openDeleteModal(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        dataSource={playbooks}
        columns={columns}
        loading={loading}
        rowKey="_id"
        pagination={{ pageSize: 10 }}
      />

      <DeleteModal
        isDeleteModal={isDeleteModal}
        showModalDelete={closeDeleteModal}
        handleDelete={confirmDelete}
        textheading="Delete Playbook"
        action="delete"
        deleteTitle={selectedPlaybook?.name}
        deleteBtn="Delete"
      />
    </>
  );
};

export default PlaybookList;
